import React from 'react';
import { motion } from 'framer-motion';
import { Workflow, Zap, Code, Layers, ArrowDown, Cpu, Lightbulb } from 'lucide-react';

const AIWorkflowResearch = ({ onClose }) => {

  const frontendEvolution = [
    {
      name: 'HTML Version',
      icon: Code,
      color: 'from-orange-500 to-red-500',
      bgColor: 'from-orange-500/20 to-red-500/20',
      borderColor: 'border-orange-500/30',
      image: '/pic/innovation/2024/workflow/html_version.webp',
      description: 'Initial implementation with native HTML',
      features: [
        'Native HTML elements',
        'Basic drag-and-drop',
        'Simple workflow designer',
        'Direct API integration'
      ]
    },
    {
      name: 'Rainbow UI Version',
      icon: Layers,
      color: 'from-blue-500 to-cyan-500',
      bgColor: 'from-blue-500/20 to-cyan-500/20',
      borderColor: 'border-blue-500/30',
      description: 'Enhanced with Rainbow UI components',
      features: [
        'Rainbow UI component library',
        'Better visual feedback',
        'Improved user experience',
        'Enhanced node connections'
      ]
    },
    {
      name: 'Angular Version',
      icon: Zap,
      color: 'from-green-500 to-emerald-500',
      bgColor: 'from-green-500/20 to-emerald-500/20',
      borderColor: 'border-green-500/30',
      image: '/pic/innovation/2024/workflow/angular_version.webp',
      description: 'Modern framework with enterprise architecture',
      features: [
        'Angular reactive framework',
        'Modular architecture',
        'TypeScript support',
        'Enterprise scalability'
      ]
    }
  ];

  const backendStack = [
    {
      name: 'LangGraph',
      icon: Workflow,
      color: 'text-purple-400',
      description: 'Python-based workflow orchestration',
      features: [
        'Graph-based workflow design',
        'State management',
        'Complex agent coordination',
        'Async workflow execution'
      ]
    },
    {
      name: 'InsureMO AI PaaS',
      icon: Cpu,
      color: 'text-cyan-400',
      description: 'Enterprise AI platform integration',
      features: [
        'PaaS adapter layer',
        'Enterprise security',
        'Scalable deployment',
        'Production monitoring'
      ]
    }
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="modal-overlay"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="modal-content max-w-6xl max-h-[90vh] overflow-y-auto pr-2 custom-scrollbar"
        onClick={(e) => e.stopPropagation()}
      >
        <button className="close-btn" onClick={onClose}>×</button>

        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Workflow className="w-8 h-8 text-green-400" />
            <h2 className="text-4xl font-bold">AI Workflow Platform</h2>
          </div>
          <p className="text-gray-300 text-lg">
            Low-Code Workflow Builder with Enterprise Integration
          </p>
        </div>

        {/* Design Inspiration - Flowise */}
        <div className="mb-8">
          <div className="bg-gradient-to-r from-yellow-500/10 to-orange-500/10 rounded-xl p-6 border border-yellow-500/30">
            <div className="flex items-center gap-3 mb-4">
              <Lightbulb className="w-6 h-6 text-yellow-400" />
              <h3 className="text-xl font-bold text-yellow-400">💡 Design Inspiration: Flowise</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div className="bg-white/5 rounded-lg p-4">
                <p className="text-orange-400 font-bold mb-2">Flowise v1 - Expert Focus</p>
                <p className="text-gray-300 text-xs">Required deep AI knowledge for workflow configuration, suitable for AI engineers and experts</p>
              </div>
              <div className="bg-white/5 rounded-lg p-4">
                <p className="text-green-400 font-bold mb-2">Flowise v2 - User-Friendly</p>
                <p className="text-gray-300 text-xs">Simplified interface making AI accessible to business users without technical background</p>
              </div>
            </div>
            <div className="mt-4 p-3 bg-yellow-500/10 rounded-lg border border-yellow-500/20">
              <p className="text-xs text-center text-gray-300">
                ✨ Inspired by Flowise's node-based design philosophy, we built our own workflow platform with enterprise-grade features
              </p>
            </div>
          </div>
        </div>

        {/* Frontend Evolution */}
        <div className="mb-8">
          <h3 className="text-2xl font-bold mb-6 text-center">🎨 Frontend Technology Evolution</h3>
          <div className="max-w-4xl mx-auto space-y-6">
            {/* HTML Version */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
              className={`bg-gradient-to-br ${frontendEvolution[0].bgColor} rounded-xl border ${frontendEvolution[0].borderColor} overflow-hidden`}
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
                {/* Left: Image */}
                <div className="relative bg-gray-900/50 p-6 flex items-center justify-center min-h-[320px]">
                  <motion.img
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.2 }}
                    src={frontendEvolution[0].image}
                    alt={frontendEvolution[0].name}
                    className="max-w-full max-h-[300px] object-contain rounded-lg border-2 border-white/10"
                  />
                </div>

                {/* Right: Content */}
                <div className="p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <div className={`bg-gradient-to-br ${frontendEvolution[0].color} p-2 rounded-lg`}>
                      <Code className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h4 className="font-bold text-lg">{frontendEvolution[0].name}</h4>
                      <p className="text-xs text-gray-400">{frontendEvolution[0].description}</p>
                    </div>
                  </div>

                  <div className="mt-4">
                    <p className="text-xs text-gray-400 mb-2">Key Features</p>
                    <ul className="space-y-1">
                      {frontendEvolution[0].features.map((feature, idx) => (
                        <li key={idx} className="text-xs text-gray-300 flex items-start gap-2">
                          <span className="text-green-400 mt-0.5">•</span>
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Down Arrow to Rainbow UI */}
            <div className="flex justify-center py-2">
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="text-center"
              >
                <ArrowDown className="w-6 h-6 text-green-400 mx-auto mb-2" />
                <p className="text-xs text-gray-400">Enhanced UI Components</p>
              </motion.div>
            </div>

            {/* Rainbow UI Version - Simple Card Only */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4 }}
              className={`bg-gradient-to-br ${frontendEvolution[1].bgColor} rounded-xl border ${frontendEvolution[1].borderColor} p-6`}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className={`bg-gradient-to-br ${frontendEvolution[1].color} p-2 rounded-lg`}>
                  <Layers className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h4 className="font-bold text-lg">{frontendEvolution[1].name}</h4>
                  <p className="text-xs text-gray-400">{frontendEvolution[1].description}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {frontendEvolution[1].features.map((feature, idx) => (
                  <div key={idx} className="bg-white/5 rounded-lg p-3">
                    <p className="text-xs text-gray-300">{feature}</p>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Down Arrow to Angular */}
            <div className="flex justify-center py-2">
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="text-center"
              >
                <ArrowDown className="w-6 h-6 text-green-400 mx-auto mb-2" />
                <p className="text-xs text-gray-400">Modern Framework Architecture</p>
              </motion.div>
            </div>

            {/* Angular Version */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.6 }}
              className={`bg-gradient-to-br ${frontendEvolution[2].bgColor} rounded-xl border ${frontendEvolution[2].borderColor} overflow-hidden`}
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
                {/* Left: Image */}
                <div className="relative bg-gray-900/50 p-6 flex items-center justify-center min-h-[320px]">
                  <motion.img
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.7 }}
                    src={frontendEvolution[2].image}
                    alt={frontendEvolution[2].name}
                    className="max-w-full max-h-[300px] object-contain rounded-lg border-2 border-white/10"
                  />
                </div>

                {/* Right: Content */}
                <div className="p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <div className={`bg-gradient-to-br ${frontendEvolution[2].color} p-2 rounded-lg`}>
                      <Zap className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h4 className="font-bold text-lg">{frontendEvolution[2].name}</h4>
                      <p className="text-xs text-gray-400">{frontendEvolution[2].description}</p>
                    </div>
                  </div>

                  <div className="mt-4">
                    <p className="text-xs text-gray-400 mb-2">Key Features</p>
                    <ul className="space-y-1">
                      {frontendEvolution[2].features.map((feature, idx) => (
                        <li key={idx} className="text-xs text-gray-300 flex items-start gap-2">
                          <span className="text-green-400 mt-0.5">•</span>
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Backend Stack */}
        <div className="mb-8">
          <h3 className="text-2xl font-bold mb-6 text-center">⚙️ Backend Architecture</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {backendStack.map((stack, index) => {
              const Icon = stack.icon;
              return (
                <motion.div
                  key={stack.name}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 rounded-xl p-6 border border-purple-500/30"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div className="bg-white/10 p-3 rounded-lg">
                      <Icon className={`w-6 h-6 ${stack.color}`} />
                    </div>
                    <div>
                      <h4 className={`font-bold text-lg ${stack.color}`}>{stack.name}</h4>
                      <p className="text-xs text-gray-400">{stack.description}</p>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <p className="text-xs text-gray-400">Key Capabilities</p>
                    <ul className="space-y-1">
                      {stack.features.map((feature, idx) => (
                        <li key={idx} className="text-xs text-gray-300 flex items-start gap-2">
                          <span className="text-purple-400 mt-0.5">•</span>
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* Architecture Overview */}
        <div className="mb-6">
          <div className="bg-gradient-to-r from-green-500/10 via-blue-500/10 to-purple-500/10 rounded-xl p-6 border border-white/20">
            <h3 className="text-xl font-bold mb-4 text-center text-white">🏗️ Platform Architecture</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div className="bg-white/5 rounded-lg p-4">
                <p className="text-cyan-400 font-bold mb-2">Frontend Layer</p>
                <p className="text-xs text-gray-300">Angular-based visual workflow designer with drag-and-drop interface</p>
              </div>
              <div className="bg-white/5 rounded-lg p-4">
                <p className="text-purple-400 font-bold mb-2">Orchestration Layer</p>
                <p className="text-xs text-gray-300">LangGraph for complex workflow state management and execution</p>
              </div>
              <div className="bg-white/5 rounded-lg p-4">
                <p className="text-green-400 font-bold mb-2">Platform Layer</p>
                <p className="text-xs text-gray-300">InsureMO AI PaaS adapter for enterprise deployment</p>
              </div>
            </div>
          </div>
        </div>

      </motion.div>
    </motion.div>
  );
};

export default AIWorkflowResearch;
